import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { DI_TOKENS } from '@constants';
import { useInjection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { CaretRightIcon, FileTextIcon } from '@phosphor-icons/react';
import { AuthStore } from '@stores';

/**
 * Page header: a breadcrumb row with the "Document Hub" / "Preview profile"
 * actions on the right, then the large "Organization Profile" title with the
 * tenant name — matching the mockup layout.
 */
export const ProfileHeader: FC = observer(() => {
  const { t } = useTrans();
  const theme = useTheme();
  const navigate = useNavigate();
  const authStore = useInjection<AuthStore>(DI_TOKENS.AuthStore);
  const tenantName = authStore.userProfile?.tenant?.name ?? '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="textSM_regular" color="text.tertiary">
            {t('app.orgProfile.breadcrumbRoot')}
          </Typography>
          <CaretRightIcon size={12} color={theme.palette.text.disabled} />
          <Typography variant="textSM_semiBold" color="text.secondary">
            {t('app.orgProfile.title')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="secondary"
            startIcon={<FileTextIcon size={16} />}
            onClick={() => navigate('/tenant-profile/document-hub')}
            sx={{ textTransform: 'none' }}
          >
            {t('app.orgProfile.documentHub')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="displaySM_bold" color="text.primary">
          {t('app.orgProfile.title')}
        </Typography>
        {tenantName && (
          <Typography variant="textMD_regular" color="text.tertiary">
            {tenantName}
          </Typography>
        )}
      </Box>
    </Box>
  );
});
